/**
 * BuyCertPage — direct-to-checkout cert purchase surface, mounted at
 * /#/buy-cert. Replaces sending GHL traffic to /#/certified (the
 * landing page), which forced an already-convinced coach through an
 * extra discovery step.
 *
 * Four states (rendered inside the same shell so the brand chrome
 * and trust signals stay consistent):
 *   1. Not signed in              : pitch + Create account / Sign in CTAs.
 *      A sessionStorage flag is set so the dashboard, after auth,
 *      routes them back here automatically.
 *   2. Signed in, no active sub   : big "Continue to checkout" button
 *      plus a downsell link to subscribe instead.
 *   3. Signed in, has active sub  : checkout button only. No downsell
 *      because they're already on the platform.
 *   4. Already owns cert          : "you already own this" + link
 *      back to /#/certified.
 *
 * Voice: no em dashes anywhere in user-facing copy.
 *
 * Visual: brand header, cinematic price, 2x2 feature grid with gold
 * icons, animated gold sweep on the card edge, subtle radial gradient
 * background, trust footer (Stripe lock + secure checkout).
 */
import React, { useEffect, useState } from "react";
import {
  Loader2, ArrowRight, CheckCircle2, ShieldCheck, Award,
  BookOpen, Infinity as InfinityIcon, MessageCircle, GraduationCap,
  Users, Globe,
} from "lucide-react";
import { GOLD } from "../constants/theme";
import { BAM_LOGO_PNG } from "../constants/branding";
import { supabase } from "../../supabaseClient";
import { logEvent } from "../utils/analytics";
import { isNative } from "../../utils/platform";
import { startCertCheckout, certPricing, fetchCountry, CERT_3PAY_COUNT } from "../utils/certCheckout";
import { captureAdSource } from "../../utils/adSource";
import { CallModal } from "./certSalesParts";
import { Phone } from "lucide-react";
import { metaTrack } from "../../utils/metaPixel";


export function BuyCertPage() {
  const [loading, setLoading] = useState(true);
  const [signedIn, setSignedIn] = useState(false);
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [alreadyOwns, setAlreadyOwns] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState(null);
  // The checkout step behind the website's Enroll buttons (2026-11-02):
  // choose how to pay, see international pricing, enter a code, or book a
  // call, then Stripe. The website does the selling; this takes payment.
  const [plan, setPlan] = useState("full");
  const [code, setCode] = useState("");
  const [codeOpen, setCodeOpen] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [country, setCountry] = useState(null);
  const price = certPricing(code.trim() ? "US" : country);
  useEffect(() => {
    captureAdSource();
    let on = true;
    fetchCountry().then((c) => { if (on) setCountry(c); });
    if (new URLSearchParams((window.location.hash.split("?")[1]) || "").get("plan") === "3pay") setPlan("3pay");
    return () => { on = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("cert_cancelled") === "1") {
        setError("Checkout was cancelled. No charge. Try again whenever you're ready.");
        const url = new URL(window.location.href);
        url.searchParams.delete("cert_cancelled");
        window.history.replaceState({}, "", url.toString());
        logEvent("cert_purchase_cancelled", { from: "buy_cert_page" });
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (cancelled) return;
      if (!user) {
        setSignedIn(false);
        setLoading(false);
        logEvent("buy_cert_view", { state: "anon" });
        return;
      }
      setSignedIn(true);
      const { data: rows } = await supabase
        .from("profiles")
        .select("stripe_subscription_active, cert_purchased")
        .eq("id", user.id)
        .limit(1);
      if (cancelled) return;
      const active = !!rows?.[0]?.stripe_subscription_active;
      const owns = !!rows?.[0]?.cert_purchased;
      setHasActiveSub(active);
      setAlreadyOwns(owns);
      setLoading(false);
      logEvent("buy_cert_view", {
        state: owns ? "owns" : active ? "subscribed" : "no_sub",
      });
    })();
    return () => { cancelled = true; };
  }, []);

  const goSignup = (mode) => {
    // Use the existing post-signin redirect mechanism that BAMFull's
    // restoreAfterSignin reads natively. Setting bam_post_signin_hash
    // makes the user land back here after auth completes, instead of
    // the default /#/platform paywall route. Plus the cert_purchase
    // intent flag as a belt-and-suspenders second mechanism for the
    // BAMFull useEffect catch.
    try {
      sessionStorage.setItem("bam_post_signin_hash", "#/buy-cert");
      sessionStorage.setItem("bam_cert_purchase_intent", "1");
    } catch {}
    logEvent("buy_cert_auth_click", { mode });
    window.location.hash = mode === "signin" ? "#/signin" : "#/signup";
  };

  // Signed in or not (2026-11-02): a signed-out visitor pays first and the
  // webhook makes their account, instead of sign up, come back, then pay.
  const goCheckout = async () => {
    setCheckoutLoading(true);
    setError(null);
    logEvent("buy_cert_checkout_start", { hasActiveSub, signedIn, plan, intl: price.intl, code: code.trim() ? code.trim().toUpperCase() : null });
    metaTrack("InitiateCheckout", { value: plan === "3pay" ? price.pay : price.full, currency: "USD", content_name: "BAM Certification" });
    const r = await startCertCheckout({ plan, returnHash: "#/buy-cert", code, country });
    if (r.alreadyPurchased) { window.location.hash = "#/certified"; return; }
    if (r.url) { window.location.href = r.url; return; }
    setError(r.error);
    setCheckoutLoading(false);
  };

  // Defensive: the cert checkout shows Stripe pricing + external payment,
  // which Apple disallows in-app. Native users are routed away at App.js;
  // this is a backstop so the page can never render on iOS. After all hooks.
  if (isNative()) return null;

  if (loading) {
    return <PageShell><BrandedSpinner/></PageShell>;
  }

  // ── 4. Already owns ─────────────────────────────────────────────
  if (alreadyOwns) {
    return (
      <PageShell>
        <Card>
          <div style={iconCircleStyle}><CheckCircle2 size={26} color={GOLD}/></div>
          <Eyebrow>You already own this</Eyebrow>
          <h1 style={smallTitleStyle}>The BAM Certification is yours</h1>
          <p style={paraStyle}>You purchased the cert already. Pick up where you left off.</p>
          <PrimaryButton onClick={() => { window.location.hash = "#/certified"; }}>
            Open my cert <ArrowRight size={15} strokeWidth={2.5}/>
          </PrimaryButton>
        </Card>
      </PageShell>
    );
  }

  const money = (n) => `$${n.toLocaleString("en-US")}`;
  const payLabel = plan === "3pay" ? `Continue, ${money(price.pay)} today` : `Continue to checkout, ${money(price.full)}`;

  return (
    <PageShell>
      <Card>
        <Eyebrow>BAM Certification</Eyebrow>
        <SocialProof/>
        {price.intl && (
          <div style={{ margin: "4px 0 14px", padding: "10px 14px", borderRadius: 10, background: `${GOLD}1A`, border: `1px solid ${GOLD}55` }}>
            <div style={{ color: GOLD, fontWeight: 800, fontSize: 15 }}>International coach pricing: {money(price.saving)} off</div>
            <div style={{ color: "#B8B2A4", fontSize: 12, marginTop: 4 }}>Applied automatically for coaches outside the US. No code needed.</div>
          </div>
        )}
        <div role="radiogroup" aria-label="Payment option" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, margin: "6px 0 16px" }}>
          {[["full", money(price.full), price.wasFull && money(price.wasFull), "Pay in full, or Klarna"],
            ["3pay", `${CERT_3PAY_COUNT} × ${money(price.pay)}`, price.wasPay && `${CERT_3PAY_COUNT} × ${money(price.wasPay)}`, "Monthly, stops after 3"]].map(([id, title, was, sub]) => (
            <button key={id} role="radio" aria-checked={plan === id} onClick={() => setPlan(id)} style={{
              padding: "14px 8px", borderRadius: 11, cursor: "pointer", color: "#F5F0E8",
              background: plan === id ? `${GOLD}18` : "transparent", border: `1.5px solid ${plan === id ? GOLD : "rgba(255,255,255,0.1)"}`,
            }}>
              {was && <div style={{ fontSize: 12, color: "#6C6658", textDecoration: "line-through" }}>{was}</div>}
              <div style={{ fontFamily: "'Bebas Neue', Impact, sans-serif", fontSize: 32, letterSpacing: 1, color: plan === id ? GOLD : "#F5F0E8", lineHeight: 1.1 }}>{title}</div>
              <div style={{ fontSize: 12, color: "#8a8578", marginTop: 2 }}>{sub}</div>
            </button>
          ))}
        </div>
        <div style={featureGridStyle}>
          <FeatureCell Icon={BookOpen}     title="Full curriculum"     sub="Unlocked the moment you pay" />
          <FeatureCell Icon={InfinityIcon} title="Lifetime access"     sub="No deadlines" />
          <FeatureCell Icon={MessageCircle} title="Staff review"       sub="Feedback on every submission" />
          <FeatureCell Icon={Award}        title="The credential"      sub="BAM Certified, plus 6 months of the platform" />
        </div>
        {hasActiveSub && (
          <CreditNote>
            Your $34.99 platform charge this month gets refunded as the cert credit.
          </CreditNote>
        )}
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <div style={{display:"flex", flexDirection:"column", gap:10, marginTop:8}}>
          <PrimaryButton onClick={goCheckout} disabled={checkoutLoading}>
            {checkoutLoading
              ? <><Loader2 size={15} style={{animation:"spin 1s linear infinite"}}/> Opening secure checkout</>
              : <>{payLabel} <ArrowRight size={15} strokeWidth={2.5}/></>}
          </PrimaryButton>
          <div style={{fontSize:12, color:"#8a8578", textAlign:"center", display:"flex", gap:6, justifyContent:"center", alignItems:"center"}}>
            <ShieldCheck size={13} color={GOLD}/> 7-day refund window{signedIn ? "" : " · No account needed, we set it up after you pay"}
          </div>
          {codeOpen ? (
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" aria-label="Discount code"
              autoCapitalize="characters" spellCheck={false}
              style={{ padding: "11px 14px", borderRadius: 10, border: "1px solid rgba(255,255,255,0.12)", background: "#0d0d0d", color: "#F5F0E8", fontSize: 15, letterSpacing: 1 }}/>
          ) : (
            <button onClick={() => setCodeOpen(true)} style={{ background: "none", border: "none", color: "#8a8578", fontSize: 12, textDecoration: "underline", cursor: "pointer" }}>
              Have a discount code?
            </button>
          )}
          <button onClick={() => { logEvent("cert_sales_call_click", { where: "buy_cert" }); setCallOpen(true); }} style={{ ...secondaryBtnStyle, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            <Phone size={14}/> Questions first? Book a 20-minute call
          </button>
          {!signedIn && (
            <button onClick={() => goSignup("signin")} style={{ background: "none", border: "none", color: "#8a8578", fontSize: 12, cursor: "pointer" }}>
              Already a member? Sign in first
            </button>
          )}
        </div>
        <Downsell signedIn={signedIn} hasActiveSub={hasActiveSub}/>
      </Card>
      <TrustFooter/>
      {callOpen && <CallModal onClose={() => setCallOpen(false)} />}
    </PageShell>
  );
}

// ── Page chrome ────────────────────────────────────────────────────

function PageShell({ children }) {
  return (
    <div style={shellStyle}>
      <style>{`
        @keyframes bamGoldSweep {
          0% { transform: translateX(-100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        @keyframes bamSpin { to { transform: rotate(360deg); } }
        @keyframes bamSpinnerPulse {
          0%, 100% { opacity: 0.45; }
          50%      { opacity: 1; }
        }
      `}</style>
      {/* Subtle radial gradient behind everything so the page doesn't
          read as a flat black box. Gold gets just a hint of glow at
          the top so the brand mark feels lit from above. */}
      <div aria-hidden="true" style={{
        position: "fixed", inset: 0, zIndex: 0,
        background: `
          radial-gradient(70% 50% at 50% 0%, rgba(226,221,159,0.06) 0%, transparent 60%),
          radial-gradient(60% 60% at 50% 100%, rgba(226,221,159,0.03) 0%, transparent 70%),
          #050505
        `,
        pointerEvents: "none",
      }}/>
      <BrandHeader/>
      <div style={{
        position: "relative", zIndex: 1,
        flex: 1, width: "100%",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "20px 16px 40px",
        gap: 18,
      }}>
        {children}
      </div>
    </div>
  );
}

function BrandHeader() {
  return (
    <div style={{
      position: "relative", zIndex: 1,
      padding: "22px 24px 14px",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 12,
    }}>
      <a href="https://byanymeanscoaches.com" target="_blank" rel="noopener noreferrer"
        style={{
          display: "inline-flex", alignItems: "center", gap: 11,
          textDecoration: "none",
          opacity: 0.92,
        }}
        title="Back to byanymeanscoaches.com">
        <img src={BAM_LOGO_PNG} alt="" style={{
          width: 28, height: 28,
          filter: `drop-shadow(0 0 8px ${GOLD}55)`,
        }}/>
        <div style={{ lineHeight: 1.05 }}>
          <div style={{
            fontFamily: "'Bebas Neue', Impact, sans-serif",
            fontSize: 16, letterSpacing: 3,
            color: GOLD,
          }}>BY ANY MEANS</div>
          <div style={{
            fontSize: 9, letterSpacing: 1.8,
            color: "#6C6658",
            textTransform: "uppercase",
            marginTop: 1,
            fontWeight: 700,
          }}>Coaches Platform</div>
        </div>
      </a>
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      position: "relative",
      maxWidth: 560, width: "100%",
      background: "linear-gradient(180deg, #131313, #0A0A0A)",
      border: `1px solid ${GOLD}55`,
      borderRadius: 20,
      boxShadow: `0 32px 80px rgba(0,0,0,0.65), inset 0 1px 0 ${GOLD}22, 0 0 0 1px rgba(0,0,0,0.4)`,
      padding: "30px 28px 26px",
      overflow: "hidden",
    }}>
      {/* Animated gold sweep along the top edge — same touch as the
          masterclass deck cards. Subtle motion that signals premium. */}
      <div aria-hidden="true" style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent 0%, ${GOLD} 50%, transparent 100%)`,
        animation: "bamGoldSweep 6s ease-in-out infinite",
      }}/>
      {children}
    </div>
  );
}

// ── Downsell (hidden when user already has active subscription) ──
function Downsell({ signedIn, hasActiveSub }) {
  if (signedIn && hasActiveSub) return null;
  const onClick = (e) => {
    e.preventDefault();
    logEvent("buy_cert_downsell_click", { signedIn });
    if (signedIn) {
      window.location.hash = "#/platform";
    } else {
      try { sessionStorage.setItem("bam_post_signup_target", "#/platform"); } catch {}
      window.location.hash = "#/signup";
    }
  };
  return (
    <div style={{
      marginTop: 20, paddingTop: 16,
      borderTop: "1px solid rgba(255,255,255,0.06)",
      textAlign: "center",
    }}>
      <div style={{fontSize: 11, color: "#6C6658", marginBottom: 4, letterSpacing: 0.3}}>
        Not ready for the cert today?
      </div>
      <a href="#/platform" onClick={onClick} style={{
        fontSize: 13, color: "#A09A8B", fontWeight: 600,
        textDecoration: "underline", cursor: "pointer",
        fontFamily: "'DM Sans', sans-serif",
      }}>
        Start with the platform ($34.99/mo), credited back if you buy the cert later
      </a>
    </div>
  );
}

function TrustFooter() {
  return (
    <div style={{
      position: "relative", zIndex: 1,
      maxWidth: 560, width: "100%",
      display: "flex", alignItems: "center", justifyContent: "center",
      gap: 14, flexWrap: "wrap",
      padding: "6px 16px 2px",
      fontSize: 11, color: "#5A5550",
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <span style={{display: "inline-flex", alignItems: "center", gap: 5}}>
        <ShieldCheck size={12} color="#5A5550"/>
        Secure checkout via Stripe
      </span>
      <span style={{opacity: 0.5}}>·</span>
      <span style={{display: "inline-flex", alignItems: "center", gap: 5}}>
        <GraduationCap size={12} color="#5A5550"/>
        Reviewed by BAM staff
      </span>
      <span style={{opacity: 0.5}}>·</span>
      <a href="mailto:team@byanymeanscoaches.com" style={{
        color: "#5A5550", textDecoration: "underline",
      }}>Questions? Email us</a>
    </div>
  );
}

function SocialProof() {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 10,
      padding: "6px 0 16px",
    }}>
      <Stat Icon={Users}  value="100+" label="Certified coaches"/>
      <span style={{ width: 1, height: 22, background: `${GOLD}33` }}/>
      <Stat Icon={Globe}  value="20+"  label="Countries"/>
    </div>
  );
}

function Stat({ Icon, value, label }) {
  return (
    <div style={{display: "inline-flex", alignItems: "center", gap: 7}}>
      <Icon size={13} color={GOLD}/>
      <span style={{
        fontSize: 13, fontWeight: 800, color: "#F5F0E8",
        fontFamily: "'JetBrains Mono', monospace",
      }}>{value}</span>
      <span style={{
        fontSize: 11, color: "#A09A8B",
        fontWeight: 500,
      }}>{label}</span>
    </div>
  );
}

function FeatureCell({ Icon, title, sub }) {
  return (
    <div style={{
      padding: "14px 14px",
      borderRadius: 11,
      background: "rgba(255,255,255,0.025)",
      border: `1px solid ${GOLD}1A`,
      display: "flex", alignItems: "flex-start", gap: 11,
    }}>
      <div style={{
        flexShrink: 0,
        width: 32, height: 32, borderRadius: 8,
        background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={15} color={GOLD}/>
      </div>
      <div style={{minWidth: 0}}>
        <div style={{
          fontSize: 12.5, fontWeight: 800, color: "#F5F0E8",
          marginBottom: 2, letterSpacing: 0.1,
        }}>{title}</div>
        <div style={{
          fontSize: 11.5, color: "#A09A8B", lineHeight: 1.4,
        }}>{sub}</div>
      </div>
    </div>
  );
}

function BrandedSpinner() {
  return (
    <div style={{
      width: 38, height: 38,
      borderRadius: "50%",
      border: `2.5px solid ${GOLD}22`,
      borderTopColor: GOLD,
      animation: "bamSpin 0.85s linear infinite",
    }}/>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 800, color: GOLD,
      letterSpacing: 1.6, textTransform: "uppercase",
      marginBottom: 10,
    }}>{children}</div>
  );
}

const iconCircleStyle = {
  width: 56, height: 56, borderRadius: 14,
  background: `${GOLD}18`, border: `1px solid ${GOLD}55`,
  display: "flex", alignItems: "center", justifyContent: "center",
  marginBottom: 18,
};

const smallTitleStyle = {
  fontSize: 24, fontWeight: 800, color: "#F5F0E8",
  margin: "0 0 12px", lineHeight: 1.2, letterSpacing: -0.3,
};

const paraStyle = {
  fontSize: 14, color: "#A09A8B", lineHeight: 1.65,
  margin: "0 0 18px",
};

const featureGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 8,
  marginBottom: 18,
};

function CreditNote({ children }) {
  return (
    <div style={{
      position: "relative",
      padding: "12px 14px 12px 38px",
      marginBottom: 16,
      borderRadius: 9,
      background: `${GOLD}10`,
      border: `1px solid ${GOLD}55`,
      fontSize: 13, color: "#F5F0E8", lineHeight: 1.5,
    }}>
      <CheckCircle2 size={16} color={GOLD} style={{
        position: "absolute", top: 13, left: 12,
      }}/>
      {children}
    </div>
  );
}

function PrimaryButton({ children, onClick, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      width: "100%", padding: "14px 0", borderRadius: 11,
      background: GOLD, color: "#0A0A0A",
      fontSize: 14, fontWeight: 800, letterSpacing: 0.3,
      border: "none", cursor: disabled ? "default" : "pointer",
      boxShadow: `0 8px 24px ${GOLD}66, inset 0 1px 0 rgba(255,255,255,0.25)`,
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
      fontFamily: "'DM Sans', sans-serif",
      opacity: disabled ? 0.7 : 1,
      transition: "transform .15s ease, box-shadow .15s ease",
    }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}>
      {children}
    </button>
  );
}

const secondaryBtnStyle = {
  width: "100%", padding: "12px 0", borderRadius: 10,
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "#A09A8B",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
  fontFamily: "'DM Sans', sans-serif",
};

function ErrorBlock({ children }) {
  return (
    <div style={{
      marginBottom: 14, padding: "10px 12px",
      borderRadius: 8, background: "rgba(248,113,113,0.08)",
      border: "1px solid rgba(248,113,113,0.25)",
      fontSize: 12.5, color: "#F87171", lineHeight: 1.5,
    }}>{children}</div>
  );
}

const shellStyle = {
  minHeight: "100vh",
  background: "#050505",
  color: "#F5F0E8",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'DM Sans', sans-serif",
  position: "relative",
};

export default BuyCertPage;
